// Tiedustelut - inquiries in customers/{cid}/inquiries, one collection for every inquiry type
// (docs/features/inquiries.md). Today the only type is 'organisationevent', sent through the
// customer integrations API; the field names are confirmed, so they are read directly.
//
// Pure: no Firebase, no React. Status labels, colours, dates and the status patch are shared
// with the recurring sale inquiries in ./inquiries.

import { isKnownStatus, toSortKey } from './inquiries'

export const INQUIRY_TYPES = [{ value: 'organisationevent', label: 'Yritystapahtumatiedustelu' }]

// A type this build does not know yet still shows, under its own name, rather than vanishing.
export const typeLabel = (value) => {
  const found = INQUIRY_TYPES.find((t) => t.value === value)
  return found ? found.label : value || 'Tiedustelu'
}

// The status filter: 'open' is the default, the ones still waiting for the venue.
export const OPEN_STATUSES = ['new', 'responded']

export const STATUS_FILTERS = [
  { value: 'open', label: 'Avoimet' },
  { value: 'new', label: 'Uudet' },
  { value: 'responded', label: 'Vastatut' },
  { value: 'done', label: 'Valmiit' },
  { value: 'all', label: 'Kaikki' },
]

const str = (value) => (value === undefined || value === null ? '' : String(value))

export const normaliseCustomerInquiry = (doc) => ({
  id: doc.id,
  type: str(doc.type),
  status: isKnownStatus(doc.status) ? doc.status : 'new',
  organisation: str(doc.organisation),
  name: str(doc.name),
  email: str(doc.email),
  phone: str(doc.phone),
  reference: str(doc.reference),
  message: str(doc.message),
  created: doc.created || null,
  createdsort: toSortKey(doc.created),
  raw: doc,
})

// Who the inquiry is from, for a heading: the company, or the person when there is none.
export const inquiryTitle = (inquiry) => (inquiry && (inquiry.organisation || inquiry.name || inquiry.email)) || 'Tiedustelu'

// The list's message column: the first non-empty line, cut at about 80 characters.
export const messagePreview = (message, max) => {
  const limit = max || 80
  const line = str(message)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find(Boolean)
  if (!line) return ''
  return line.length > limit ? line.slice(0, limit - 1).trimEnd() + '…' : line
}

// A status change made by the partner through the API is stamped 'api:<last4>' with no email.
export const isApiAuthor = (value) => str(value).startsWith('api:')

export const authorLabel = (value) => (isApiAuthor(value) ? 'Rajapinta (kumppani)' : str(value))

export const matchesStatusFilter = (inquiry, filter) => {
  if (!filter || filter === 'all') return true
  if (filter === 'open') return OPEN_STATUSES.includes(inquiry.status)
  return inquiry.status === filter
}

// Free text over the fields a person would search by. Every word must match somewhere.
export const matchesSearch = (inquiry, search) => {
  const words = str(search).toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const haystack = [inquiry.organisation, inquiry.name, inquiry.email, inquiry.phone, inquiry.reference, inquiry.message].join(' ').toLowerCase()
  return words.every((w) => haystack.includes(w))
}

export const filterInquiries = (inquiries, { status, type, search } = {}) =>
  (inquiries || []).filter((i) => matchesStatusFilter(i, status) && (!type || type === 'all' || i.type === type) && matchesSearch(i, search))

// The types present in the data, known ones first, for the type filter.
export const typesIn = (inquiries) => {
  const present = Array.from(new Set((inquiries || []).map((i) => i.type).filter(Boolean)))
  const known = INQUIRY_TYPES.map((t) => t.value).filter((v) => present.includes(v))
  return [...known, ...present.filter((v) => !known.includes(v)).sort()]
}

export const mailtoHref = (inquiry) =>
  inquiry && inquiry.email ? 'mailto:' + inquiry.email + '?subject=' + encodeURIComponent('Tiedustelunne: ' + inquiryTitle(inquiry)) : ''

// tel: wants the number without spaces or dashes; the partner's free-form text is shown as is.
export const telHref = (phone) => {
  const digits = str(phone).replace(/[^\d+]/g, '')
  return digits ? 'tel:' + digits : ''
}
